<script lang="ts">
import formFieldsInit from '../../utils/formFieldsInit.vue'
import FormLabel from './label.vue'

export default {
    name: 'SelectField',
    components: { FormLabel },
    extends: formFieldsInit,
    props: {
        noTranslation: { type: Boolean },
        disableSortOptions: { type: Boolean },
        radioButtons: { type: Boolean, required: false },
    },
    emits: ['updateData'],
    computed: {
        setValue: {
            get() {
                if (this.value)
                    return this.value

                return null
            },
            set(newValue) {
                this.$emit('updateData', newValue)
            },
        },
        showValue() {
            if (this.setValue) {
                if (this.noTranslation || this.isMultiple || this.isFieldMultiple)
                    return this.setValue
                else
                    return this.$translate(`${this.translationName ?? this.injectedTranslationName}.${this.dataKey}.${this.setValue}`)
            }
            return null
        },
        items() {
            if (this.rule) {
                let values = this.rule.find(r => r.includes('in:') && !r.includes('min:'))
                if (values) {
                    values = values.replace('in:', '')
                    let items
                    try {
                        items = JSON.parse(`[${values}]`)
                    }
                    catch {
                        items = values.split(',').map((v: string) => v.trim()) // string beginning by 0 dont work with first parse
                    }
                    if (!this.disableSortOptions)
                        items = items.sort((a: string, b: string) => String(a).localeCompare(String(b)))

                    return items
                }
                // return items.map(item => ({name : item, showable : this.$translate(`${this.translationName}.${this.dataKey}.${item}`), color : 'blue'}))
            }
            return []
        },
        field() {
            return this.formService?.entity?.meta?.fields?.find(
                field => field.name === this.dataKey,
            )
        },
        isTag(): boolean {
            return !!this.field?.is_tag
        },
        isFieldMultiple(): boolean {
            return !this.rule?.includes('max:1') && this.rule?.includes('array')
        },
    },
    methods: {
        getOptionColor(title: string): string | undefined {
            return this.field?.options?.find(option => option.title === title)?.color
        },
    },
}
</script>

<template>
    <FormLabel v-if="(data || injectedData || formService?.data) && (value !== undefined || rule)">
        <template v-if="rule">
            <v-radio-group
                v-if="radioButtons"
                :id="customId ?? dataKey"
                v-model="setValue"
                :name="customId ?? dataKey"
                :disabled="disabled"
                density="compact"
                :error-messages="getErrors()"
                :hint="getInfo()"
                @update:model-value="$emit('updateData', $event); updateData($event)"
                @input="validation && validation.$touch()"
                @blur="validation && validation.$touch()"
            >
                <template v-for="(item, index) in items" :key="item">
                    <v-radio :value="item">
                        <template #label>
                            <div class="ml-2">
                                <v-chip
                                    v-if="isChips || isTag"
                                    :class="index < (items.length - 1) ? 'mb-2' : ''"
                                    :variant="$config.public.chips_variant || 'tonal'"
                                    :color="getOptionColor(item) || $getColor(`${translationName}.${dataKey}.${item}`)"
                                >
                                    {{ noTranslation ? item : $translate(`${translationName}.${dataKey}.${item}`) }}
                                </v-chip>
                                <span v-else-if="noTranslation">{{ item }}</span>
                                <span v-else-if="showKey">{{ $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${showKey}.${item}`) }}</span>
                                <span v-else>{{ $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${item}`) }}</span>
                            </div>
                        </template>
                    </v-radio>
                </template>
            </v-radio-group>
            <v-select
                v-else
                :id="customId ?? dataKey"
                v-model="setValue"
                :name="customId ?? dataKey"
                :disabled="disabled"
                :items="items"
                :clearable="isClearable"
                variant="outlined"
                density="compact"
                :multiple="isMultiple || isFieldMultiple"
                :error-messages="getErrors()"
                :hint="getInfo()"
                persistent-hint
                @update:model-value="$emit('updateData', $event); updateData($event)"
                @input="validation && validation.$touch()"
                @blur="validation && validation.$touch() || injectedValidation?.$touch();"
            >
                <!-- TEMPLATE IN DROPDOWN -->
                <template #item="{ props, item }">
                    <v-list-item v-bind="props">
                        <v-list-item-title class="select-options">
                            <v-chip
                                v-if="isChips || isTag"
                                :variant="$config.public.chips_variant || 'tonal'"
                                :color="getOptionColor(item.title) || $getColor(`${translationName}.${dataKey}.${item.value}`)"
                            >
                                {{ noTranslation ? item.value : $translate(`${translationName}.${dataKey}.${item.value}`) }}
                            </v-chip>
                            <span v-else-if="noTranslation">{{ item.value }}</span>
                            <span v-else-if="showKey">{{ $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${showKey}.${item.value}`) }}</span>
                            <span v-else>{{ $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${item.value}`) }}</span>
                        </v-list-item-title>
                    </v-list-item>
                </template>
                <!-- TEMPLATE IN SELECT INPUT -->
                <template #selection="{ item, index }">
                    <v-chip
                        v-if="isChips || isTag"
                        :variant="$config.public.chips_variant || 'tonal'"
                        :color="getOptionColor(item.title) || $getColor(`${translationName}.${dataKey}.${item.value}`)"
                    >
                        {{ noTranslation ? item.value : $translate(`${translationName}.${dataKey}.${item.value}`) }}
                    </v-chip>
                    <template v-else>
                        <span v-if="noTranslation">{{ item.value }}</span>
                        <span v-else-if="showKey">{{ $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${showKey}.${item.value}`) }}</span>
                        <span v-else>{{ $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${item.value}`) }}</span>
                        <span v-if="index < (setValue.length - 1) && (isMultiple || isFieldMultiple)">, </span>
                    </template>
                </template>
            </v-select>
        </template>

        <div v-else-if="showValue">
            <!-- IF MULTIPLE -->
            <template v-if="Array.isArray(showValue)">
                <template v-if="withTooltip">
                    <v-tooltip
                        v-for="val in showValue"
                        :key="val"
                        :text="$translate(`${translationName ?? injectedTranslationName}.${dataKey}.tooltip.${val}`)"
                    >
                        <template #activator="{ props }">
                            <v-chip
                                v-if="isChips || isTag"
                                v-bind="props"
                                :variant="$config.public.chips_variant || 'tonal'"
                                :color="getOptionColor(val) || $getColor(val)"
                                class="mr-1"
                            >
                                {{ val }}
                            </v-chip>
                            <span
                                v-else
                                v-bind="props"
                            > {{ val }}</span>
                        </template>
                    </v-tooltip>
                </template>
                <template v-else>
                    <template
                        v-if="isChips || isTag"
                    >
                        <v-chip
                            v-for="(val) in showValue"
                            :key="val"
                            :class="isTag ? 'mt-1 mr-2' : 'mr-2'"
                            :variant="$config.public.chips_variant || 'tonal'"
                            :color="getOptionColor(val) || $getColor(`${translationName ?? injectedTranslationName}.${dataKey}.${val}`)"
                        >
                            {{ noTranslation ? val : $translate(`${translationName ?? injectedTranslationName}.${dataKey}.${val}`) }}
                        </v-chip>
                    </template>
                    <div v-else class="">
                        <p
                            v-for="(val) in showValue"
                            :key="val"
                        >
                            {{ noTranslation ? val : $translate(`${translationName}.${dataKey}.${val}`) }}
                        </p>
                    </div>
                    <div v-if="!showValue.length">
                        <small class="font-italic text-disabled">-</small>
                    </div>
                </template>
            </template>

            <!-- IF SINGLE -->
            <template v-else-if="withTooltip">
                <v-tooltip :text="$translate(`${translationName ?? injectedTranslationName}.${dataKey}.tooltip.${value}`)">
                    <template #activator="{ props }">
                        <v-chip
                            v-if="isChips || isTag"
                            v-bind="props"
                            :variant="$config.public.chips_variant || 'tonal'"
                            :color="getOptionColor(showValue) || $getColor(showValue)"
                        >
                            {{ showValue }}
                        </v-chip>
                        <span
                            v-else
                            v-bind="props"
                        > {{ showValue }}</span>
                    </template>
                </v-tooltip>
            </template>
            <template v-else>
                <v-chip
                    :class="isTag ? 'mt-1' : 'mb-2'"
                    v-if="isChips || isTag"
                    :variant="$config.public.chips_variant || 'tonal'"
                    :color="getOptionColor(showValue) || $getColor(`${translationName ?? injectedTranslationName}.${dataKey}.${value}`)"
                >
                    {{ showValue }}
                </v-chip>
                <span v-else>{{ showValue }}</span>
            </template>
        </div>
        <div v-else>
            <small class="font-italic text-disabled">-</small>
        </div>
    </FormLabel>
</template>
